/**
 * Game Error Codes
 *
 * Centralized error codes and messages for consistent user feedback.
 * All game logic should throw errors using these codes.
 */

export const GameErrorCode = {
  // Pet state errors
  PET_NOT_FOUND: 'PET_NOT_FOUND',
  PET_IS_EGG: 'PET_IS_EGG',
  PET_IS_HIBERNATING: 'PET_IS_HIBERNATING',

  // Health/condition errors
  PET_TOO_SICK_TO_TRAIN: 'PET_TOO_SICK_TO_TRAIN',
  PET_TOO_SICK_TO_REST: 'PET_TOO_SICK_TO_REST',
  PET_TOO_TIRED_TO_TRAIN: 'PET_TOO_TIRED_TO_TRAIN',

  // Rest errors
  PET_TOO_HUNGRY_TO_REST: 'PET_TOO_HUNGRY_TO_REST',
  PET_TOO_ENERGETIC_TO_REST: 'PET_TOO_ENERGETIC_TO_REST',
  PET_ALREADY_RESTING: 'PET_ALREADY_RESTING',
  PET_NOT_RESTING: 'PET_NOT_RESTING',

  // Cooldown errors
  ENERGY_REFILL_ON_COOLDOWN: 'ENERGY_REFILL_ON_COOLDOWN',

  // Evolution errors
  NOT_ENOUGH_XP_FOR_EVOLUTION: 'NOT_ENOUGH_XP_FOR_EVOLUTION',
  NOT_ENOUGH_XP_FOR_LEVEL_UP: 'NOT_ENOUGH_XP_FOR_LEVEL_UP',
  ALREADY_MAX_STAGE: 'ALREADY_MAX_STAGE',

  // API/Server errors
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  ADOPTION_LIMIT_EXCEEDED: 'ADOPTION_LIMIT_EXCEEDED',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  INVALID_SPECIES: 'INVALID_SPECIES',
  INVALID_RARITY: 'INVALID_RARITY',
  MINIMUM_PAYMENT_REQUIRED: 'MINIMUM_PAYMENT_REQUIRED',
  RARE_PET_PAYMENT_REQUIRED: 'RARE_PET_PAYMENT_REQUIRED',
  EPIC_PET_PAYMENT_REQUIRED: 'EPIC_PET_PAYMENT_REQUIRED',
  LEGENDARY_PET_PAYMENT_REQUIRED: 'LEGENDARY_PET_PAYMENT_REQUIRED',
  NOT_PET_OWNER: 'NOT_PET_OWNER',
  TRANSACTION_NOT_FOUND: 'TRANSACTION_NOT_FOUND',
  TRANSACTION_SENDER_MISMATCH: 'TRANSACTION_SENDER_MISMATCH',
  NO_PAYMENT_DETECTED: 'NO_PAYMENT_DETECTED',
  PAYMENT_AMOUNT_MISMATCH: 'PAYMENT_AMOUNT_MISMATCH',
  TRANSACTION_ALREADY_PROCESSED: 'TRANSACTION_ALREADY_PROCESSED',
  NFT_NOT_FOUND: 'NFT_NOT_FOUND',

  // Generic errors
  INVALID_SUIKA_AMOUNT: 'INVALID_SUIKA_AMOUNT',
  INSUFFICIENT_SUIKA: 'INSUFFICIENT_SUIKA',
  INVALID_COMBAT_ACTION: 'INVALID_COMBAT_ACTION',
  INVALID_REST_STATE: 'INVALID_REST_STATE',
  SPECIES_CONFIG_NOT_FOUND: 'SPECIES_CONFIG_NOT_FOUND',
  CONCURRENT_MODIFICATION: 'CONCURRENT_MODIFICATION',
  SAVE_FAILED: 'SAVE_FAILED',
  MAX_RETRIES_EXCEEDED: 'MAX_RETRIES_EXCEEDED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type GameErrorCodeType = (typeof GameErrorCode)[keyof typeof GameErrorCode];

/**
 * User-friendly error messages for each error code.
 */
export const ERROR_MESSAGES: Record<GameErrorCodeType, string | ((data?: any) => string)> = {
  // Pet state errors
  [GameErrorCode.PET_NOT_FOUND]: 'Suikamon not found. Please refresh and try again.',
  [GameErrorCode.PET_IS_EGG]: 'Your Suikamon needs to hatch first!',
  [GameErrorCode.PET_IS_HIBERNATING]: 'Your Suikamon is hibernating! Send Suika to wake it up.',

  // Health/condition errors
  [GameErrorCode.PET_TOO_SICK_TO_TRAIN]: 'Your Suikamon is too sick to train. Give it medicine first!',
  [GameErrorCode.PET_TOO_SICK_TO_REST]: 'Your Suikamon is too sick to sleep. Give it medicine first!',
  [GameErrorCode.PET_TOO_TIRED_TO_TRAIN]: 'Your Suikamon is too tired to train. Let it rest or refill energy.',

  // Rest errors
  [GameErrorCode.PET_TOO_HUNGRY_TO_REST]: 'Your Suikamon is too hungry to sleep. Feed it first!',
  [GameErrorCode.PET_TOO_ENERGETIC_TO_REST]: 'Your Suikamon has too much energy to sleep. Try training or playing first.',
  [GameErrorCode.PET_ALREADY_RESTING]: 'Your Suikamon is already sleeping.',
  [GameErrorCode.PET_NOT_RESTING]: 'Your Suikamon is not sleeping right now.',

  // Cooldown errors
  [GameErrorCode.ENERGY_REFILL_ON_COOLDOWN]: (data?: { timeRemaining?: string }) =>
    `Energy refill on cooldown. Try again in ${data?.timeRemaining || 'a while'}.`,

  // Evolution errors
  [GameErrorCode.NOT_ENOUGH_XP_FOR_EVOLUTION]: (data?: { currentXp?: number; requiredXp?: number; remainingXp?: number }) =>
    `Not enough XP to evolve${data?.remainingXp ? `. Need ${data.remainingXp} more XP` : ''}.`,
  [GameErrorCode.NOT_ENOUGH_XP_FOR_LEVEL_UP]: (data?: { currentXp?: number; requiredXp?: number; remainingXp?: number }) =>
    `Not enough XP to level up${data?.remainingXp ? `. Need ${data.remainingXp} more XP` : ''}.`,
  [GameErrorCode.ALREADY_MAX_STAGE]: 'Your Suikamon is already at maximum evolution stage!',

  // API/Server errors
  [GameErrorCode.RATE_LIMIT_EXCEEDED]: 'You are performing actions too quickly. Please wait a moment before trying again.',
  [GameErrorCode.ADOPTION_LIMIT_EXCEEDED]: 'Adoption limit reached. You can adopt up to 5 pets per hour. Please try again later.',
  [GameErrorCode.MISSING_REQUIRED_FIELD]: (data?: { field?: string }) =>
    `Missing required field${data?.field ? `: ${data.field}` : ''}`,
  [GameErrorCode.INVALID_SPECIES]: 'Invalid species. Please select a valid pet species.',
  [GameErrorCode.INVALID_RARITY]: 'Invalid rarity. Please select a valid rarity level.',
  [GameErrorCode.MINIMUM_PAYMENT_REQUIRED]: 'Minimum 0.09 SUI required to adopt a pet. This covers blockchain costs and initial pet gas.',
  [GameErrorCode.RARE_PET_PAYMENT_REQUIRED]: 'Rare pets require at least 0.76 SUI adoption contribution.',
  [GameErrorCode.EPIC_PET_PAYMENT_REQUIRED]: 'Epic pets require at least 3.2 SUI adoption contribution.',
  [GameErrorCode.LEGENDARY_PET_PAYMENT_REQUIRED]: 'Legendary pets can only be adopted with the correct SUI contribution.',
  [GameErrorCode.NOT_PET_OWNER]: 'You can only perform this action on your own pets.',
  [GameErrorCode.TRANSACTION_NOT_FOUND]: 'Transaction not found or failed. Please try again.',
  [GameErrorCode.TRANSACTION_SENDER_MISMATCH]: 'Transaction sender does not match pet owner. You can only replenish Suika for your own pets.',
  [GameErrorCode.NO_PAYMENT_DETECTED]: 'No SUI transfer detected in transaction. Please try again.',
  [GameErrorCode.PAYMENT_AMOUNT_MISMATCH]: (data?: { expected?: number; received?: number }) =>
    `Payment verification failed. Expected ${data?.expected} SUI, received ${data?.received} SUI`,
  [GameErrorCode.TRANSACTION_ALREADY_PROCESSED]: 'Transaction already processed. Suika has already been added for this payment.',
  [GameErrorCode.NFT_NOT_FOUND]: 'Pet NFT not found on blockchain. Please refresh and try again.',

  // Generic errors
  [GameErrorCode.INVALID_SUIKA_AMOUNT]: 'Invalid Suika amount. Please try again.',
  [GameErrorCode.INSUFFICIENT_SUIKA]: (data?: { required?: number; current?: number }) =>
    `Not enough Suika! ${data?.required ? `Need ${data.required} Suika` : 'Replenish Suika'} to perform this action.`,
  [GameErrorCode.INVALID_COMBAT_ACTION]: 'Invalid combat action. Please try again.',
  [GameErrorCode.INVALID_REST_STATE]: 'Something went wrong. Please try again.',
  [GameErrorCode.SPECIES_CONFIG_NOT_FOUND]: 'Something went wrong. Please try again later.',
  [GameErrorCode.CONCURRENT_MODIFICATION]: 'Another action is in progress. Please try again.',
  [GameErrorCode.SAVE_FAILED]: 'Failed to save. Please try again.',
  [GameErrorCode.MAX_RETRIES_EXCEEDED]: 'Action failed after multiple attempts. Please try again later.',
  [GameErrorCode.UNKNOWN_ERROR]: 'Something went wrong. Please try again.',
};

/**
 * Custom error class that includes an error code
 */
export class GameError extends Error {
  constructor(
    public code: GameErrorCodeType,
    public data?: any
  ) {
    const messageOrFn = ERROR_MESSAGES[code];
    const message = typeof messageOrFn === 'function' ? messageOrFn(data) : messageOrFn;
    super(message);
    this.name = 'GameError';
  }
}

/**
 * Helper to get error message from error code
 */
export function getErrorMessage(code: GameErrorCodeType, data?: any): string {
  const messageOrFn = ERROR_MESSAGES[code];
  return typeof messageOrFn === 'function' ? messageOrFn(data) : messageOrFn;
}
